import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getStoreProfitability } from '../api/client';
import { formatCurrency, formatPercentage, exportToCSV } from '../utils/formatters';

const StoreProfitability = () => {
  const [profitabilityData, setProfitabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  const fetchProfitabilityData = async () => {
    try {
      setLoading(true);
      const startDate = dateRange.startDate.toISOString().split('T')[0];
      const endDate = dateRange.endDate.toISOString().split('T')[0];

      const data = await getStoreProfitability(startDate, endDate);
      setProfitabilityData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching store profitability:', err);
      setError('Error al cargar la rentabilidad por tienda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitabilityData();
  }, [dateRange]);

  const handleDateChange = (field, date) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const exportData = () => {
    const exportableData = profitabilityData.map(store => ({
      'Tienda': store.store_name,
      'Ubicación': store.store_location,
      'Ventas Total': store.total_sales,
      'Costo de lo Vendido': store.cost_of_goods_sold || store.total_inventory_cost || 0,
      'Gastos Operativos': store.total_store_expenses,
      'Ganancia Bruta': store.gross_profit,
      'Ganancia Neta': store.net_profit,
      'Margen Bruto (%)': store.gross_margin.toFixed(2),
      'Margen Neto (%)': store.net_margin.toFixed(2),
      'Meta Mensual': store.monthly_goal,
      'Cumplimiento Meta (%)': store.goal_achievement.toFixed(2)
    }));
    
    exportToCSV(exportableData, 'rentabilidad_por_tienda');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Calculando rentabilidad...</span>
      </div>
    );
  }

  // Prepare chart data - use cost_of_goods_sold (costo de lo vendido)
  const chartData = profitabilityData.map(store => ({
    tienda: store.store_name.length > 15 ? store.store_name.substring(0, 15) + '...' : store.store_name,
    ventas: store.total_sales,
    costoVendido: store.cost_of_goods_sold || store.total_inventory_cost || 0,
    gastosOperativos: store.total_store_expenses,
    gananciaNeta: store.net_profit
  }));

  const totalSales = profitabilityData.reduce((sum, store) => sum + store.total_sales, 0);
  const totalCostOfGoodsSold = profitabilityData.reduce((sum, store) => sum + (store.cost_of_goods_sold || store.total_inventory_cost || 0), 0);
  const totalOperationalExpenses = profitabilityData.reduce((sum, store) => sum + store.total_store_expenses, 0);
  const totalNetProfit = profitabilityData.reduce((sum, store) => sum + store.net_profit, 0);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🏆 Rentabilidad por Tienda</h1>
          <p className="text-gray-600 mt-1">
            Análisis detallado de ganancias netas considerando inventario y gastos operativos
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchProfitabilityData} className="btn-outline">
            🔄 Actualizar
          </button>
          {profitabilityData.length > 0 && (
            <button onClick={exportData} className="btn-primary">
              📥 Exportar Datos
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📅 Período de Análisis</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={dateRange.startDate}
              onChange={(date) => handleDateChange('startDate', date)}
              className="form-input"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={dateRange.endDate}
              onChange={(date) => handleDateChange('endDate', date)}
              className="form-input"
              dateFormat="dd/MM/yyyy"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="stat-card">
          <div className="stat-card-icon bg-green-500">💰</div>
          <div className="stat-card-value">{formatCurrency(totalSales)}</div>
          <div className="stat-card-label">Ventas Totales</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon bg-blue-500">📦</div>
          <div className="stat-card-value">{formatCurrency(totalCostOfGoodsSold)}</div>
          <div className="stat-card-label">Costo de lo Vendido</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon bg-red-500">📊</div>
          <div className="stat-card-value">{formatCurrency(totalOperationalExpenses)}</div>
          <div className="stat-card-label">Gastos Operativos</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon bg-purple-500">🎯</div>
          <div className={`stat-card-value ${totalNetProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(totalNetProfit)}
          </div>
          <div className="stat-card-label">Ganancia Neta Total</div>
        </div>
      </div>

      {/* Profitability Chart */}
      {profitabilityData.length > 0 && (
        <div className="chart-container">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Análisis Comparativo por Tienda</h3>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart 
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="tienda" 
                angle={-45}
                textAnchor="end"
                height={120}
                interval={0}
                fontSize={12}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                width={80}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const labels = {
                    ventas: 'Ventas',
                    costoVendido: 'Costo de lo Vendido',
                    gastosOperativos: 'Gastos Operativos',
                    gananciaNeta: 'Ganancia Neta'
                  };
                  return [formatCurrency(value), labels[name] || name];
                }}
              />
              <Bar dataKey="ventas" fill="#10B981" name="ventas" />
              <Bar dataKey="costoVendido" fill="#EF4444" name="costoVendido" />
              <Bar dataKey="gastosOperativos" fill="#F59E0B" name="gastosOperativos" />
              <Bar dataKey="gananciaNeta" fill="#8B5CF6" name="gananciaNeta" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detailed Table */}
      {profitabilityData.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏪</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos de rentabilidad</h3>
          <p className="text-gray-600">
            Para generar el análisis de rentabilidad necesitas:
          </p>
          <ul className="text-sm text-gray-500 mt-2 space-y-1">
            <li>• Tiendas registradas</li>
            <li>• Ventas registradas por tienda</li>
            <li>• Compras de inventario por tienda</li>
          </ul>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📋 Detalle de Rentabilidad por Tienda</h3>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Ventas</th>
                  <th>Costo de lo Vendido</th>
                  <th>Gastos Operativos</th>
                  <th>Ganancia Bruta</th>
                  <th>Ganancia Neta</th>
                  <th>Margen Neto</th>
                  <th>Meta/Cumplimiento</th>
                </tr>
              </thead>
              <tbody>
                {profitabilityData.map((store) => (
                  <tr key={store.store_id}>
                    <td>
                      <div>
                        <div className="font-semibold text-gray-900">{store.store_name}</div>
                        <div className="text-sm text-gray-600">{store.store_location}</div>
                      </div>
                    </td>
                    <td>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(store.total_sales)}
                      </span>
                      <div className="text-xs text-gray-500">
                        {store.sales_count} ventas
                        {store.sales_percentage !== undefined && (
                          <span> ({formatPercentage(store.sales_percentage)})</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="font-semibold text-red-600">
                        {formatCurrency(store.cost_of_goods_sold || store.total_inventory_cost || 0)}
                      </span>
                      <div className="text-xs text-gray-500">
                        costo de lo vendido
                      </div>
                    </td>
                    <td>
                      <span className="font-semibold text-orange-600">
                        {formatCurrency(store.total_store_expenses)}
                      </span>
                      {(store.direct_expenses !== undefined || store.proportional_expenses !== undefined) && (
                        <div className="text-xs text-gray-500">
                          <div>Directos: {formatCurrency(store.direct_expenses || 0)}</div>
                          <div>Generales: {formatCurrency(store.proportional_expenses || 0)}</div>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`font-semibold ${store.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(store.gross_profit)}
                      </span>
                      <div className="text-xs text-gray-500">
                        {formatPercentage(store.gross_margin)} margen
                      </div>
                      {(() => {
                        const cogs = store.cost_of_goods_sold || store.total_inventory_cost || 0;
                        const markup = cogs > 0 ? (store.gross_profit / cogs * 100) : 0;
                        return (
                          <div className="text-xs text-gray-400">
                            {markup.toFixed(1)}% markup
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <span className={`font-semibold ${store.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(store.net_profit)}
                      </span>
                      <div className="text-xs text-gray-500">
                        {formatPercentage(store.net_margin)} margen
                      </div>
                    </td>
                    <td>
                      <div className={`text-center ${
                        store.net_margin >= 20 ? 'text-green-600' : 
                        store.net_margin >= 10 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {store.net_margin >= 20 ? '🟢 Excelente' : 
                         store.net_margin >= 10 ? '🟡 Bueno' : 
                         store.net_margin >= 0 ? '🟠 Regular' : '🔴 Pérdidas'}
                      </div>
                    </td>
                    <td>
                      {store.monthly_goal > 0 ? (
                        <div>
                          <div className="text-sm font-medium">
                            {formatCurrency(store.monthly_goal)}
                          </div>
                          <div className={`text-xs ${
                            store.goal_achievement >= 100 ? 'text-green-600' : 
                            store.goal_achievement >= 80 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {formatPercentage(store.goal_achievement)} cumplido
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin meta</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {profitabilityData.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">💡 Recomendaciones</h3>
          </div>
          <div className="space-y-3">
            {(() => {
              const bestStore = profitabilityData.reduce((prev, current) => 
                (prev.net_profit > current.net_profit) ? prev : current
              );
              const worstStore = profitabilityData.reduce((prev, current) => 
                (prev.net_profit < current.net_profit) ? prev : current
              );
              
              return (
                <>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800">
                      <strong>🏆 Mejor desempeño:</strong> {bestStore.store_name} con {formatCurrency(bestStore.net_profit)} 
                      de ganancia neta ({formatPercentage(bestStore.net_margin)} margen). 
                      Considera replicar sus estrategias en otras tiendas.
                    </p>
                  </div>
                  
                  {worstStore.net_profit < 0 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800">
                        <strong>⚠️ Atención requerida:</strong> {worstStore.store_name} tiene pérdidas de {formatCurrency(Math.abs(worstStore.net_profit))}. 
                        Revisa los costos de inventario y gastos operativos.
                      </p>
                    </div>
                  )}
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800">
                      <strong>📊 Análisis general:</strong> Margen neto promedio: {formatPercentage(totalNetProfit / totalSales * 100)}. 
                      El costo de lo vendido representa el {formatPercentage(totalCostOfGoodsSold / totalSales * 100)} de las ventas.
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreProfitability;